import type { FailCodeData } from '@packages/domain/techline/cases';
import type { VinDetailEntity } from '@packages/domain/vin';

import { ANALYTICS_EVENTS } from '@packages/core/analytics';
import { Button, Typography } from '@packages/ui/shared';
import { analyticsService } from '@web/services/singletons';
import { useCallback } from 'react';

import type { FailCodeSelection } from './fail-codes-modal/fail-codes-modal.types';

import { CollapsibleSection } from '../collapsible-section';
import { FailCodeCard } from './fail-code-card';
import { FailCodesModal } from './fail-codes-modal/fail-codes-modal';

/** Style for the fail code error text spacing. */
const failCodeErrorStyle = { marginTop: 8 };

/**
 * Props for {@link FailCodeSection}.
 */
export type FailCodeSectionProps = {
    /** Whether the user can edit the submitted case. Users with the DSQM role cannot edit submitted cases. */
    canEditSubmittedCase: boolean;

    /** The current fail code data, or null if no fail code is set. */
    failCodeData: FailCodeData | null;

    /** Validation errors from fail code operations. */
    failCodeErrors: string[];

    /** Whether the case currently has a fail code set. */
    hasFailCode: boolean;

    /** Whether the case is in a closed status. */
    isCaseClosed: boolean;

    /** Callback to clear the current fail code from the case. */
    onClearFailCode: () => Promise<void>;

    /** Callback to close the fail code selection modal. */
    onCloseFailCodeModal: () => void;

    /** Callback to open the fail code selection modal. */
    onOpenFailCodeModal: () => void;

    /** Callback to update the fail code with new data. */
    onUpdateFailCode: (data: FailCodeData) => Promise<void>;

    /** Whether the fail code selection modal is currently visible. */
    showFailCodeModal: boolean;

    /** VIN details for the vehicle; required for the fail code modal's lookups. */
    vehicleVinData: VinDetailEntity | undefined;
};

/**
 * Renders the "Fail Code" section of the submitted case view. Wraps the section header
 * (with add button), the fail code card (when a fail code is set), validation errors,
 * and the fail code selection modal into a single cohesive block.
 *
 * Handles the analytics logging for adding a fail code and adapts the modal's
 * {@link FailCodeSelection} shape to the domain's {@link FailCodeData} shape before
 * forwarding to the parent.
 *
 * @param props - {@link FailCodeSectionProps}
 * @returns the fail code section JSX
 */
export function FailCodeSection({
    canEditSubmittedCase,
    failCodeData,
    failCodeErrors,
    hasFailCode,
    isCaseClosed,
    onClearFailCode,
    onCloseFailCodeModal,
    onOpenFailCodeModal,
    onUpdateFailCode,
    showFailCodeModal,
    vehicleVinData,
}: FailCodeSectionProps) {
    // Forwarded callback aliases
    const handleClearFailCode = onClearFailCode;
    const handleCloseFailCodeModal = onCloseFailCodeModal;

    // Handlers

    /**
     * Log an analytics event and open the fail code modal.
     */
    const handleAddFailCodeButtonClick = useCallback(() => {
        analyticsService.logEvent(ANALYTICS_EVENTS.USER_STARTS_ADDING_FAILCODE);
        onOpenFailCodeModal();
    }, [onOpenFailCodeModal]);

    /**
     * Adapt the modal's {@link FailCodeSelection} to the domain's {@link FailCodeData}
     * shape, log an analytics event, and forward to the parent.
     *
     * @param selection - the fail code selection from the modal
     */
    const handleFailCodeModalConfirm = useCallback(
        (selection: FailCodeSelection) => {
            // Build the FailCodeData from the modal's selection shape
            const adaptedFailCodeData: FailCodeData = {
                failCode: selection.failCode,
                repairCode: selection.repairCode,
                section: selection.section ?? '',
                subSection: selection.subSection ?? '',
            };
            analyticsService.logEvent(ANALYTICS_EVENTS.USER_SUCCESSFULLY_ADDS_FAILCODE, {
                fail_code_category: adaptedFailCodeData.section,
            });
            onUpdateFailCode(adaptedFailCodeData);
        },
        [onUpdateFailCode]
    );

    // JSX
    return (
        <>
            <CollapsibleSection
                collapsible={false}
                rightAction={
                    <Button
                        disabled={isCaseClosed || hasFailCode}
                        onPress={handleAddFailCodeButtonClick}
                        title={'Add Fail Code'}
                        variant="primary"
                    />
                }
                showDividerBottom
                showDividerTop
                showRightAction={canEditSubmittedCase}
                title={
                    <div className="d-flex align-items-center">
                        <Typography variant="h3">Fail Code</Typography>
                        {!hasFailCode && (
                            <>
                                {/* eslint-disable-next-line no-restricted-syntax -- single-property style (convention exception) */}
                                <Typography color="redOne" style={{ marginLeft: 4 }}>
                                    *
                                </Typography>
                                <Typography
                                    color="textDarkSecondary"
                                    // eslint-disable-next-line no-restricted-syntax -- single-property style (convention exception)
                                    style={{ marginLeft: 8 }}
                                    variant="labelRegular"
                                >
                                    This field is required for closing the case
                                </Typography>
                            </>
                        )}
                    </div>
                }
            />

            {failCodeData?.repairCode && failCodeData.section && (
                <FailCodeCard
                    code={failCodeData.repairCode}
                    description={failCodeData.repairCode}
                    disableDelete={isCaseClosed}
                    onDelete={handleClearFailCode}
                    section={failCodeData.section}
                    showDelete={canEditSubmittedCase}
                    subsection={failCodeData.subSection}
                />
            )}

            {failCodeErrors?.length ? (
                <Typography color="redOne" style={failCodeErrorStyle}>
                    {failCodeErrors[0]}
                </Typography>
            ) : null}

            {vehicleVinData && (
                <FailCodesModal
                    onClose={handleCloseFailCodeModal}
                    onConfirmFailCode={handleFailCodeModalConfirm}
                    show={showFailCodeModal}
                    vehicleVinData={vehicleVinData}
                />
            )}
        </>
    );
}
